// Domain-scoped landers list (MIS-810 / Task 29).
//
// Shows one domain's landers. The header reuses the domain row (host + brand +
// status) with a quick link to the domain's Integrations page; "New Lander"
// opens the NewLanderDialog → `POST /api/landers` with this domain's id in the
// body (there is NO `/api/domains/:domainId/landers` route). The list is fetched
// fresh via React Query, scoped to this domain with `?domainId=` (the repo is
// already tenant-scoped). Delete → confirm Dialog → `DELETE /api/landers/:id`
// (HARD delete). 403s surface as toasts; the API enforces RBAC.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { DomainStatusBadge } from '@/components/domains/DomainStatusBadge';
import { formatTimestamp } from '@/components/domains/format';
import { NewLanderDialog } from '@/components/landers/NewLanderDialog';
import { type Lander, TEMPLATE_LABELS } from '@/components/landers/types';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { DataTable } from '@/components/ui/DataTable';
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { useToast } from '@/components/ui/toast';
import { useApi } from '@/lib/api';

export const Route = createFileRoute('/_authenticated/domains/$domainId/landers')({
  component: DomainLandersPage,
});

function DomainLandersPage(): React.ReactElement {
  const { domainId } = Route.useParams();
  const api = useApi();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [newOpen, setNewOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Lander | null>(null);

  const { data: domainData, isError: domainError } = useQuery({
    queryKey: ['domains', 'detail', domainId],
    queryFn: async () => {
      const res = await api.api.domains[':domainId'].$get({ param: { domainId } });
      if (!res.ok) throw new Error(`Failed to load domain (${res.status})`);
      return res.json();
    },
  });
  const domain = domainData?.domain;

  const {
    data: landers,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['landers', 'byDomain', domainId],
    queryFn: async () => {
      const res = await api.api.landers.$get({ query: { domainId } });
      if (!res.ok) throw new Error('Failed to load landers');
      const data = await res.json();
      return data.landers;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (landerId: number) => {
      const res = await api.api.landers[':id'].$delete({ param: { id: String(landerId) } });
      if (!res.ok) {
        if (res.status === 403) throw new Error("You don't have edit access to this lander.");
        throw new Error(`Delete failed (${res.status})`);
      }
      return res.json();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['landers'] });
      toast({ title: 'Lander deleted', variant: 'success' });
      setPendingDelete(null);
    },
    onError: (err: Error) => {
      toast({ title: 'Could not delete lander', description: err.message, variant: 'destructive' });
    },
  });

  const columns = useMemo<ColumnDef<Lander, unknown>[]>(
    () => [
      {
        accessorKey: 'slug',
        header: 'Slug',
        cell: ({ row }) => (
          <Link
            to="/landers/$landerId/edit"
            params={{ landerId: String(row.original.id) }}
            className="font-mono font-medium text-gray-900 hover:underline"
          >
            {row.original.slug}
          </Link>
        ),
      },
      {
        accessorKey: 'headline',
        header: 'Headline',
        cell: ({ row }) => row.original.headline ?? <span className="text-gray-400">—</span>,
      },
      {
        id: 'template',
        header: 'Template',
        cell: ({ row }) => <Badge variant="gray">{TEMPLATE_LABELS[row.original.template]}</Badge>,
      },
      {
        id: 'updated',
        header: 'Last updated',
        cell: ({ row }) => {
          const ts = row.original.updatedAt ?? row.original.createdAt;
          return <span className="text-gray-500">{ts ? formatTimestamp(ts) : '—'}</span>;
        },
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="flex justify-end gap-2">
            <Link to="/landers/$landerId/edit" params={{ landerId: String(row.original.id) }}>
              <Button variant="ghost" size="sm">
                Edit
              </Button>
            </Link>
            <Button
              variant="ghost"
              size="sm"
              className="text-red-600 hover:bg-red-50"
              onClick={() => setPendingDelete(row.original)}
            >
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-8">
      <div>
        <Link
          to="/domains/$domainId"
          params={{ domainId }}
          className="text-sm text-gray-500 hover:underline"
        >
          ← Domain
        </Link>
      </div>

      {/* Domain header card (host + brand + status) with an Integrations link. */}
      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-0 space-y-1">
            <div className="flex items-center gap-3">
              <span className="font-mono text-lg font-semibold text-gray-900">
                {domain?.host ?? (domainError ? 'Unknown domain' : '…')}
              </span>
              {domain && <DomainStatusBadge domain={domain} />}
            </div>
            {domain?.brandName && <p className="text-sm text-gray-500">{domain.brandName}</p>}
          </div>
          <Link to="/domains/$domainId/integrations" params={{ domainId }}>
            <Button variant="outline" size="sm">
              Integrations
            </Button>
          </Link>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Landers</h1>
          <p className="text-sm text-gray-500">Landing pages on this domain.</p>
        </div>
        <Button onClick={() => setNewOpen(true)}>New Lander</Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading landers…</p>
      ) : isError ? (
        <p className="text-sm text-red-600">Failed to load landers. Try again.</p>
      ) : (
        <DataTable
          columns={columns}
          data={landers ?? []}
          emptyMessage="No landers on this domain yet. Create your first one."
        />
      )}

      <NewLanderDialog domainId={Number(domainId)} open={newOpen} onOpenChange={setNewOpen} />

      <Dialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        ariaLabel="Confirm delete lander"
      >
        <DialogHeader>
          <DialogTitle>Delete lander</DialogTitle>
          <DialogDescription>
            This permanently removes the lander. It can't be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <p className="text-sm text-gray-700">
            Delete <span className="font-mono font-medium">{pendingDelete?.slug}</span>?
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => setPendingDelete(null)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={deleteMutation.isPending}
            onClick={() => pendingDelete && deleteMutation.mutate(pendingDelete.id)}
          >
            {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}
